import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    ComposedChart,
    Area,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    ReferenceDot,
} from "recharts";
import { getNetWorth } from "../api/plaidClient";
import { getSavingsRate } from "../api/savingsClient";
import { getPreferences } from "../api/profileClient";
import { formatCurrencyShort, formatCurrencyCompact } from "../utils/formatters";

// ============================================================================
// CONSTANTS
// ============================================================================

const ASSET_RETURNS = { stocks: 10, bonds: 5, cash: 2 };
const INFLATION_RATE = 3; // Average annual inflation rate

const SCENARIOS = {
    conservative: { name: "Conservative", volatility: 8, allocation: { stocks: 30, bonds: 60, cash: 10 } },
    moderate: { name: "Moderate", volatility: 12, allocation: { stocks: 60, bonds: 30, cash: 10 } },
    aggressive: { name: "Aggressive", volatility: 18, allocation: { stocks: 90, bonds: 5, cash: 5 } },
};

const MILESTONES = [100000, 250000, 500000, 750000, 1000000, 2000000, 5000000];

// ============================================================================
// CALCULATIONS
// ============================================================================

function calculateExpectedReturn(allocation) {
    return (
        (allocation.stocks / 100) * ASSET_RETURNS.stocks +
        (allocation.bonds / 100) * ASSET_RETURNS.bonds +
        (allocation.cash / 100) * ASSET_RETURNS.cash
    );
}

function runProjection({ startingAmount, monthlyContribution, annualIncrease, expectedReturn, volatility, years, simulations = 1000 }) {
    // Convert annual return to monthly using geometric formula: (1 + r)^(1/12) - 1
    const monthlyReturn = Math.pow(1 + expectedReturn / 100, 1 / 12) - 1;
    const monthlyVol = (volatility / 100) / Math.sqrt(12);
    const totalMonths = years * 12;

    function randomNormal() {
        let u = 0, v = 0;
        while (u === 0) u = Math.random();
        while (v === 0) v = Math.random();
        return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
    }

    const allPaths = [];
    for (let sim = 0; sim < simulations; sim++) {
        const path = [];
        let balance = startingAmount;
        let contribution = monthlyContribution;
        let totalContributions = startingAmount;

        for (let month = 0; month <= totalMonths; month++) {
            if (month > 0 && month % 12 === 0) {
                contribution *= (1 + annualIncrease / 100);
            }
            if (month > 0) {
                const r = monthlyReturn + monthlyVol * randomNormal();
                balance = balance * (1 + r) + contribution;
                totalContributions += contribution;
            }
            if (month % 12 === 0) {
                path.push({ year: month / 12, balance, contributions: totalContributions });
            }
        }
        allPaths.push(path);
    }

    const data = [];
    for (let y = 0; y <= years; y++) {
        const values = allPaths.map(p => p[y].balance).sort((a, b) => a - b);
        const contributions = allPaths[0][y].contributions;
        data.push({
            year: y,
            p10: Math.round(values[Math.floor(simulations * 0.1)]),
            p50: Math.round(values[Math.floor(simulations * 0.5)]),
            p90: Math.round(values[Math.floor(simulations * 0.9)]),
            contributions: Math.round(contributions),
            range: [Math.round(values[Math.floor(simulations * 0.1)]), Math.round(values[Math.floor(simulations * 0.9)])],
        });
    }

    return data;
}

function findYearToReachGoal(data, goal) {
    for (let i = 0; i < data.length; i++) {
        if (data[i].p50 >= goal) return { year: i, amount: data[i].p50 };
    }
    return null;
}

function calculateProbability({ startingAmount, targetAmount, monthlyContribution, annualIncrease, years, expectedReturn, volatility, simulations = 1000 }) {
    // Convert annual return to monthly using geometric formula: (1 + r)^(1/12) - 1
    const monthlyReturn = Math.pow(1 + expectedReturn / 100, 1 / 12) - 1;
    const monthlyVol = (volatility / 100) / Math.sqrt(12);
    const totalMonths = years * 12;

    function randomNormal() {
        let u = 0, v = 0;
        while (u === 0) u = Math.random();
        while (v === 0) v = Math.random();
        return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
    }

    let success = 0;
    for (let sim = 0; sim < simulations; sim++) {
        let balance = startingAmount;
        let contribution = monthlyContribution;
        for (let month = 0; month < totalMonths; month++) {
            if (month > 0 && month % 12 === 0) contribution *= (1 + annualIncrease / 100);
            const r = monthlyReturn + monthlyVol * randomNormal();
            balance = balance * (1 + r) + contribution;
        }
        if (balance >= targetAmount) success++;
    }
    return (success / simulations) * 100;
}

function getRelevantMilestones(current, projected) {
    return MILESTONES.filter(m => m > current && m <= projected * 1.2);
}

// Deterministic projection for what-if comparisons (no random variance)
function runDeterministicProjection({ startingAmount, monthlyContribution, annualIncrease, expectedReturn, years }) {
    const monthlyReturn = Math.pow(1 + expectedReturn / 100, 1 / 12) - 1;
    const totalMonths = years * 12;

    let balance = startingAmount;
    let contribution = monthlyContribution;

    for (let month = 0; month < totalMonths; month++) {
        if (month > 0 && month % 12 === 0) {
            contribution *= (1 + annualIncrease / 100);
        }
        balance = balance * (1 + monthlyReturn) + contribution;
    }

    return Math.round(balance);
}

// Adjust a value for inflation to show in today's dollars
function adjustForInflation(amount, years) {
    return amount / Math.pow(1 + INFLATION_RATE / 100, years);
}

// Adjust projection data for inflation (each year's values discounted to today's dollars)
function adjustProjectionForInflation(data) {
    return data.map(d => ({
        ...d,
        p10: Math.round(adjustForInflation(d.p10, d.year)),
        p50: Math.round(adjustForInflation(d.p50, d.year)),
        p90: Math.round(adjustForInflation(d.p90, d.year)),
        contributions: Math.round(adjustForInflation(d.contributions, d.year)),
        range: [
            Math.round(adjustForInflation(d.range[0], d.year)),
            Math.round(adjustForInflation(d.range[1], d.year))
        ],
    }));
}

// ============================================================================
// FORMATTING (uses shared formatters from utils/formatters.js)
// ============================================================================

// ============================================================================
// COMPONENTS
// ============================================================================

function InsightCard({ icon, label, value, subtext, color = "text-gray-900", tooltip }) {
    return (
        <div className="text-center relative group">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center justify-center gap-1">
                {label}
                {tooltip && (
                    <svg className="w-4 h-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                )}
            </p>
            <p className={`text-3xl font-semibold tracking-tight ${color}`}>{value}</p>
            {subtext && <p className="text-sm text-gray-500 mt-2">{subtext}</p>}
            {tooltip && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 rounded-lg text-xs text-white w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 text-left shadow-lg">
                    {tooltip}
                </div>
            )}
        </div>
    );
}

function ProgressBar({ current, goal }) {
    const progress = Math.min((current / goal) * 100, 100);
    return (
        <div className="w-full">
            <div className="flex justify-between text-sm text-gray-600 mb-3">
                <span>Progress to goal</span>
                <span className="font-medium">{progress.toFixed(0)}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                    className="h-full bg-accent-500 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                />
            </div>
            <div className="flex justify-between text-sm mt-3">
                <span className="text-gray-500">{formatCurrencyShort(current)}</span>
                <span className="text-gray-500">{formatCurrencyShort(goal)}</span>
            </div>
        </div>
    );
}

function ScenarioCard({ id, label, description, enabled, amount, onToggle, onAmountChange, prefix = "$", suffix = "" }) {
    return (
        <div className={`p-4 rounded-xl border transition-colors ${enabled ? 'bg-accent-50 border-accent-200' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex-1 min-w-0 mr-3">
                    <p className={`text-sm font-medium ${enabled ? 'text-gray-900' : 'text-gray-600'}`}>{label}</p>
                    {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
                </div>
                <label className="relative cursor-pointer flex-shrink-0">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={() => onToggle(id)}
                        className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-accent-500 transition-colors" />
                    <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                </label>
            </div>
            {enabled && onAmountChange && (
                <div className="mt-3">
                    <div className="relative">
                        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{prefix}</span>}
                        <input
                            type="number"
                            value={amount || ""}
                            onChange={(e) => onAmountChange(id, parseInt(e.target.value) || 0)}
                            className="input w-full text-sm pl-7 pr-16"
                            step={50}
                        />
                        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{suffix}</span>}
                    </div>
                </div>
            )}
        </div>
    );
}

function ProjectionChart({ data, goal, milestones, showRange, selectedMilestone }) {
    const maxVal = Math.max(...data.map(d => d.p90), goal || 0) * 1.1;

    // Find the year when selected milestone is reached
    const milestonePoint = selectedMilestone ? (() => {
        for (let i = 0; i < data.length; i++) {
            if (data[i].p50 >= selectedMilestone) {
                return { year: data[i].year, value: data[i].p50, milestone: selectedMilestone };
            }
        }
        return null;
    })() : null;

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const d = payload[0]?.payload;
            if (!d) return null;
            return (
                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-md">
                    <p className="text-gray-900 font-semibold mb-2">Year {label}</p>
                    <div className="space-y-1 text-sm">
                        <div className="flex justify-between gap-4">
                            <span className="text-gray-500">Expected:</span>
                            <span className="text-accent-600 font-medium">{formatCurrencyCompact(d.p50)}</span>
                        </div>
                        <div className="flex justify-between gap-4">
                            <span className="text-gray-500">Contributed:</span>
                            <span className="text-gray-700">{formatCurrencyCompact(d.contributions)}</span>
                        </div>
                        <div className="flex justify-between gap-4">
                            <span className="text-gray-500">Growth:</span>
                            <span className="text-gray-900 font-medium">{formatCurrencyCompact(d.p50 - d.contributions)}</span>
                        </div>
                        {showRange && (
                            <div className="border-t border-gray-200 pt-2 mt-2 text-xs text-gray-500">
                                Range: {formatCurrencyShort(d.p10)} - {formatCurrencyShort(d.p90)}
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <defs>
                        <linearGradient id="rangeGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#2563EB" stopOpacity={0.18} />
                            <stop offset="100%" stopColor="#2563EB" stopOpacity={0.02} />
                        </linearGradient>
                    </defs>

                    <XAxis
                        dataKey="year"
                        stroke="#94A3B8"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(y) => y === 0 ? 'Now' : `${y}y`}
                    />
                    <YAxis
                        stroke="#94A3B8"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v) => formatCurrencyShort(v)}
                        domain={[0, maxVal]}
                        width={60}
                    />
                    <Tooltip content={<CustomTooltip />} />

                    {/* Goal line */}
                    {goal > 0 && (
                        <ReferenceLine
                            y={goal}
                            stroke="#F59E0B"
                            strokeDasharray="6 4"
                            strokeWidth={2}
                        />
                    )}

                    {/* Milestone lines */}
                    {milestones.map((m) => (
                        <ReferenceLine
                            key={m}
                            y={m}
                            stroke="#E2E8F0"
                            strokeDasharray="3 3"
                            strokeWidth={1}
                        />
                    ))}

                    {/* Confidence band */}
                    {showRange && (
                        <>
                            <Area
                                type="monotone"
                                dataKey="range"
                                fill="url(#rangeGradient)"
                                stroke="none"
                            />
                            {/* P90 upper bound line */}
                            <Line
                                type="monotone"
                                dataKey="p90"
                                stroke="#2563EB"
                                strokeWidth={1}
                                strokeOpacity={0.35}
                                dot={false}
                            />
                            {/* P10 lower bound line */}
                            <Line
                                type="monotone"
                                dataKey="p10"
                                stroke="#2563EB"
                                strokeWidth={1}
                                strokeOpacity={0.35}
                                dot={false}
                            />
                        </>
                    )}

                    {/* Main projection line */}
                    <Line
                        type="monotone"
                        dataKey="p50"
                        stroke="#2563EB"
                        strokeWidth={2.5}
                        dot={false}
                    />

                    {/* Starting point marker */}
                    <ReferenceDot
                        x={0}
                        y={data[0]?.p50}
                        r={5}
                        fill="#2563EB"
                        stroke="#FFFFFF"
                        strokeWidth={2}
                    />

                    {/* Selected milestone marker */}
                    {milestonePoint && (
                        <ReferenceDot
                            x={milestonePoint.year}
                            y={milestonePoint.value}
                            r={7}
                            fill="#F59E0B"
                            stroke="#FFFFFF"
                            strokeWidth={2}
                            label={{
                                value: `${formatCurrencyShort(milestonePoint.milestone)}`,
                                position: 'top',
                                fill: '#B45309',
                                fontSize: 12,
                                fontWeight: 600,
                                offset: 15,
                            }}
                        />
                    )}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}

function SettingsPanel({ isOpen, onClose, settings, setSettings }) {
    if (!isOpen) return null;

    const scenario = Object.entries(SCENARIOS).find(
        ([_, s]) => JSON.stringify(s.allocation) === JSON.stringify(settings.allocation)
    )?.[0] || 'custom';

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-gray-900/40" onClick={onClose} />
            <div className="relative w-full max-w-sm bg-white border-l border-gray-200 p-6 overflow-y-auto shadow-lg">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="space-y-6">
                    {/* Starting Amount */}
                    <div>
                        <label className="text-sm text-gray-600 block mb-2">Starting Amount</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                            <input
                                type="number"
                                value={settings.startingAmount ?? ""}
                                onChange={(e) => setSettings({ ...settings, startingAmount: e.target.value === "" ? null : parseInt(e.target.value) || 0 })}
                                placeholder="Using linked account data"
                                className="input w-full pl-7"
                                step={10000}
                            />
                        </div>
                        {settings.startingAmount !== null && (
                            <button
                                onClick={() => setSettings({ ...settings, startingAmount: null })}
                                className="text-xs text-accent-600 hover:text-accent-700 mt-1"
                            >
                                Reset to linked data
                            </button>
                        )}
                    </div>

                    {/* Goal */}
                    <div>
                        <label className="text-sm text-gray-600 block mb-2">Your Goal</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                            <input
                                type="number"
                                value={settings.goal || ""}
                                onChange={(e) => setSettings({ ...settings, goal: parseInt(e.target.value) || 0 })}
                                className="input w-full pl-7"
                                step={50000}
                            />
                        </div>
                    </div>

                    {/* Monthly contribution */}
                    <div>
                        <label className="text-sm text-gray-600 block mb-2">Monthly Contribution</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                            <input
                                type="number"
                                value={settings.monthlyContribution || ""}
                                onChange={(e) => setSettings({ ...settings, monthlyContribution: parseInt(e.target.value) || 0 })}
                                className="input w-full pl-7"
                                step={100}
                            />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{formatCurrencyCompact(settings.monthlyContribution * 12)}/year</p>
                    </div>

                    {/* Annual increase */}
                    <div>
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Annual increase</span>
                            <span className="text-gray-900 font-medium">{settings.annualIncrease}%</span>
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={10}
                            step={0.5}
                            value={settings.annualIncrease}
                            onChange={(e) => setSettings({ ...settings, annualIncrease: parseFloat(e.target.value) })}
                            className="w-full accent-accent-500"
                        />
                    </div>

                    {/* Time horizon */}
                    <div>
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Time horizon</span>
                            <span className="text-gray-900 font-medium">{settings.years} years</span>
                        </div>
                        <input
                            type="range"
                            min={5}
                            max={40}
                            step={5}
                            value={settings.years}
                            onChange={(e) => setSettings({ ...settings, years: parseInt(e.target.value) })}
                            className="w-full accent-accent-500"
                        />
                    </div>

                    {/* Risk profile presets */}
                    <div>
                        <label className="text-sm text-gray-600 block mb-3">Risk profile</label>
                        <div className="grid grid-cols-3 gap-2">
                            {Object.entries(SCENARIOS).map(([key, s]) => (
                                <button
                                    key={key}
                                    onClick={() => setSettings({
                                        ...settings,
                                        allocation: s.allocation,
                                        volatility: s.volatility,
                                    })}
                                    className={`w-full px-2 py-2 text-xs font-medium rounded-lg transition-colors text-center ${
                                        scenario === key
                                            ? 'bg-accent-500 text-white'
                                            : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    {s.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Custom allocation sliders */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <label className="text-sm text-gray-600">Asset allocation</label>
                            {scenario === 'custom' && (
                                <span className="text-xs px-2 py-0.5 bg-accent-50 text-accent-600 font-medium rounded">Custom</span>
                            )}
                        </div>

                        {/* Stocks slider */}
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-700">Stocks</span>
                                <span className="text-gray-900 font-medium">{settings.allocation.stocks}%</span>
                            </div>
                            <input
                                type="range"
                                min={0}
                                max={100}
                                step={5}
                                value={settings.allocation.stocks}
                                onChange={(e) => {
                                    const stocks = parseInt(e.target.value);
                                    const remaining = 100 - stocks;
                                    // Distribute remaining between bonds and cash proportionally
                                    const oldBondsCash = settings.allocation.bonds + settings.allocation.cash;
                                    let bonds, cash;
                                    if (oldBondsCash > 0) {
                                        bonds = Math.round((settings.allocation.bonds / oldBondsCash) * remaining);
                                        cash = remaining - bonds;
                                    } else {
                                        bonds = Math.round(remaining * 0.7);
                                        cash = remaining - bonds;
                                    }
                                    // Calculate volatility based on stock allocation
                                    const volatility = 6 + (stocks * 0.14);
                                    setSettings({
                                        ...settings,
                                        allocation: { stocks, bonds, cash },
                                        volatility: Math.round(volatility),
                                    });
                                }}
                                className="w-full accent-accent-500"
                            />
                        </div>

                        {/* Bonds slider */}
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-700">Bonds</span>
                                <span className="text-gray-900 font-medium">{settings.allocation.bonds}%</span>
                            </div>
                            <input
                                type="range"
                                min={0}
                                max={100 - settings.allocation.stocks}
                                step={5}
                                value={settings.allocation.bonds}
                                onChange={(e) => {
                                    const bonds = parseInt(e.target.value);
                                    const cash = 100 - settings.allocation.stocks - bonds;
                                    const volatility = 6 + (settings.allocation.stocks * 0.14);
                                    setSettings({
                                        ...settings,
                                        allocation: { ...settings.allocation, bonds, cash },
                                        volatility: Math.round(volatility),
                                    });
                                }}
                                className="w-full accent-accent-400"
                            />
                        </div>

                        {/* Cash (read-only, fills remainder) */}
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-700">Cash</span>
                                <span className="text-gray-900 font-medium">{settings.allocation.cash}%</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-accent-500 rounded-full transition-all"
                                    style={{ width: `${settings.allocation.cash}%` }}
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Automatically fills remainder</p>
                        </div>

                        {/* Expected return display */}
                        <div className="pt-2 border-t border-gray-200">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Expected return</span>
                                <span className="text-accent-600 font-semibold">
                                    {((settings.allocation.stocks / 100) * 10 +
                                      (settings.allocation.bonds / 100) * 5 +
                                      (settings.allocation.cash / 100) * 2).toFixed(1)}%
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                Based on stocks 10%, bonds 5%, cash 2%
                            </p>
                        </div>
                    </div>

                    {/* Show range toggle */}
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Show confidence range</span>
                        <label className="relative cursor-pointer">
                            <input
                                type="checkbox"
                                checked={settings.showRange}
                                onChange={(e) => setSettings({ ...settings, showRange: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-accent-500 transition-colors" />
                            <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                        </label>
                    </div>

                    {/* Inflation adjustment toggle */}
                    <div className="flex items-center justify-between">
                        <div>
                            <span className="text-sm text-gray-700">Adjust for inflation</span>
                            <p className="text-xs text-gray-500">Show values in today's dollars</p>
                        </div>
                        <label className="relative cursor-pointer">
                            <input
                                type="checkbox"
                                checked={settings.adjustForInflation}
                                onChange={(e) => setSettings({ ...settings, adjustForInflation: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-accent-500 transition-colors" />
                            <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ProjectionsPage() {
    const [loading, setLoading] = useState(true);
    const [currentWealth, setCurrentWealth] = useState(0);
    const [wealthError, setWealthError] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [selectedMilestone, setSelectedMilestone] = useState(null);
    const [savingsData, setSavingsData] = useState(null);
    const [settingsInitialized, setSettingsInitialized] = useState(false);

    const [settings, setSettings] = useState({
        startingAmount: null,
        goal: 1000000,
        monthlyContribution: 1000,
        annualIncrease: 2,
        years: 20,
        allocation: SCENARIOS.moderate.allocation,
        volatility: SCENARIOS.moderate.volatility,
        showRange: true,
        adjustForInflation: false,
    });

    useEffect(() => {
        async function fetchData() {
            try {
                const [nwData, savingsResp, prefs] = await Promise.all([
                    getNetWorth().catch(() => null),
                    getSavingsRate(6).catch(() => null),
                    getPreferences().catch(() => null),
                ]);

                if (nwData) {
                    const wealth = (nwData.investmentBalance || 0) + (nwData.cashBalance || 0);
                    setCurrentWealth(wealth);
                    setWealthError(false);
                } else {
                    setWealthError(true);
                }

                if (!settingsInitialized) {
                    if (savingsResp) {
                        setSavingsData(savingsResp);
                    }
                    // Priority: real savings data > onboarding preference > hardcoded $1000
                    if (savingsResp?.avgMonthlySavings > 0) {
                        setSettings(prev => ({
                            ...prev,
                            monthlyContribution: Math.round(savingsResp.avgMonthlySavings),
                        }));
                        setSettingsInitialized(true);
                    } else if (prefs?.monthlyInvestmentAmount) {
                        setSettings(prev => ({
                            ...prev,
                            monthlyContribution: parseInt(prefs.monthlyInvestmentAmount),
                        }));
                        setSettingsInitialized(true);
                    }
                }
            } catch (e) {
                console.error("Error fetching data:", e);
                setWealthError(true);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounce settings changes by 300ms so Monte Carlo doesn't rerun on every slider tick
    const [debouncedSettings, setDebouncedSettings] = useState(settings);
    const debounceRef = useRef(null);
    useEffect(() => {
        debounceRef.current = setTimeout(() => {
            setDebouncedSettings(settings);
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [settings]);

    const expectedReturn = useMemo(() => calculateExpectedReturn(debouncedSettings.allocation), [debouncedSettings.allocation]);

    // For what-if calculations, use deterministic projection (no random variance)
    const effectiveWealth = debouncedSettings.startingAmount ?? currentWealth;

    const baseFinalDeterministic = useMemo(() => {
        return runDeterministicProjection({
            startingAmount: effectiveWealth,
            monthlyContribution: debouncedSettings.monthlyContribution,
            annualIncrease: debouncedSettings.annualIncrease,
            expectedReturn: expectedReturn,
            years: debouncedSettings.years,
        });
    }, [effectiveWealth, debouncedSettings, expectedReturn]);

    // Scenario Builder state
    const [scenarios, setScenarios] = useState({
        increaseSavings: { enabled: false, amount: 0 },
        cutExpenses: { enabled: false, amount: 0 },
        max401k: { enabled: false, amount: 1958 },
        lumpSum: { enabled: false, amount: 10000 },
        rothIRA: { enabled: false, amount: 583 },
        aggressiveAllocation: { enabled: false, amount: 0 },
    });

    // Initialize scenario defaults when settings/savingsData are ready
    useEffect(() => {
        setScenarios(prev => ({
            ...prev,
            increaseSavings: {
                ...prev.increaseSavings,
                amount: Math.round(settings.monthlyContribution * 0.25) || 250,
            },
            cutExpenses: {
                ...prev.cutExpenses,
                amount: savingsData ? Math.round(savingsData.avgMonthlyExpenses * 0.2) : 0,
            },
        }));
    }, [settings.monthlyContribution, savingsData]);

    const handleScenarioToggle = (id) => {
        setScenarios(prev => ({
            ...prev,
            [id]: { ...prev[id], enabled: !prev[id].enabled },
        }));
    };

    const handleScenarioAmountChange = (id, amount) => {
        setScenarios(prev => ({
            ...prev,
            [id]: { ...prev[id], amount },
        }));
    };

    // Build effective parameters (base + any active scenarios)
    const anyScenarioEnabled = Object.values(scenarios).some(s => s.enabled);

    const effectiveParams = useMemo(() => {
        let contribution = debouncedSettings.monthlyContribution;
        let starting = effectiveWealth;
        let returnRate = expectedReturn;
        let volatility = debouncedSettings.volatility;

        if (scenarios.increaseSavings.enabled) contribution += scenarios.increaseSavings.amount;
        if (scenarios.cutExpenses.enabled) contribution += scenarios.cutExpenses.amount;
        if (scenarios.max401k.enabled) contribution += scenarios.max401k.amount;
        if (scenarios.lumpSum.enabled) starting += scenarios.lumpSum.amount;
        if (scenarios.rothIRA.enabled) contribution += scenarios.rothIRA.amount;
        if (scenarios.aggressiveAllocation.enabled) {
            returnRate = calculateExpectedReturn(SCENARIOS.aggressive.allocation);
            volatility = SCENARIOS.aggressive.volatility;
        }

        return { contribution, starting, returnRate, volatility };
    }, [scenarios, effectiveWealth, debouncedSettings.monthlyContribution, debouncedSettings.volatility, expectedReturn]);

    // Monte Carlo projection uses effective (scenario-adjusted) params
    const projection = useMemo(() => {
        return runProjection({
            startingAmount: effectiveParams.starting,
            monthlyContribution: effectiveParams.contribution,
            annualIncrease: debouncedSettings.annualIncrease,
            expectedReturn: effectiveParams.returnRate,
            volatility: effectiveParams.volatility,
            years: debouncedSettings.years,
        });
    }, [effectiveParams, debouncedSettings]);

    const probability = useMemo(() => {
        return calculateProbability({
            startingAmount: effectiveParams.starting,
            targetAmount: debouncedSettings.goal,
            monthlyContribution: effectiveParams.contribution,
            annualIncrease: debouncedSettings.annualIncrease,
            years: debouncedSettings.years,
            expectedReturn: effectiveParams.returnRate,
            volatility: effectiveParams.volatility,
        });
    }, [effectiveParams, debouncedSettings]);

    // Apply inflation adjustment if enabled
    const displayProjection = useMemo(() => {
        return debouncedSettings.adjustForInflation ? adjustProjectionForInflation(projection) : projection;
    }, [projection, debouncedSettings.adjustForInflation]);

    // Goal is in nominal dollars, so always use nominal projection to find when goal is reached
    const goalReach = useMemo(() => findYearToReachGoal(projection, debouncedSettings.goal), [projection, debouncedSettings.goal]);
    const finalAmount = displayProjection[displayProjection.length - 1]?.p50 || 0;
    const milestones = useMemo(() => getRelevantMilestones(currentWealth, finalAmount), [currentWealth, finalAmount]);
    const monthlyRetirementIncome = Math.round((finalAmount * 0.04) / 12);

    // Scenario impact summary (comparing scenario projection to baseline)
    const scenarioImpact = useMemo(() => {
        if (!anyScenarioEnabled) return null;

        // Run a baseline deterministic for comparison
        const baselineFinal = baseFinalDeterministic;
        const scenarioFinal = runDeterministicProjection({
            startingAmount: effectiveParams.starting,
            monthlyContribution: effectiveParams.contribution,
            annualIncrease: debouncedSettings.annualIncrease,
            expectedReturn: effectiveParams.returnRate,
            years: debouncedSettings.years,
        });

        let difference = scenarioFinal - baselineFinal;
        if (debouncedSettings.adjustForInflation) {
            difference = Math.round(adjustForInflation(difference, debouncedSettings.years));
        }

        // Base display uses deterministic baseline (adjusted for inflation if needed)
        let baseFinalDisplay = baselineFinal;
        if (debouncedSettings.adjustForInflation) {
            baseFinalDisplay = Math.round(adjustForInflation(baselineFinal, debouncedSettings.years));
        }
        const scenarioFinalDisplay = baseFinalDisplay + difference;

        // Calculate years sooner/later to reach goal
        let yearsDiff = 0;
        // Find when baseline reaches goal
        let baseYears = null;
        for (let y = 1; y <= debouncedSettings.years; y++) {
            const v = runDeterministicProjection({
                startingAmount: currentWealth,
                monthlyContribution: debouncedSettings.monthlyContribution,
                annualIncrease: debouncedSettings.annualIncrease,
                expectedReturn: expectedReturn,
                years: y,
            });
            if (v >= debouncedSettings.goal) { baseYears = y; break; }
        }
        if (baseYears) {
            for (let y = 1; y <= debouncedSettings.years; y++) {
                const v = runDeterministicProjection({
                    startingAmount: effectiveParams.starting,
                    monthlyContribution: effectiveParams.contribution,
                    annualIncrease: debouncedSettings.annualIncrease,
                    expectedReturn: effectiveParams.returnRate,
                    years: y,
                });
                if (v >= debouncedSettings.goal) { yearsDiff = baseYears - y; break; }
            }
        }

        const baseRetirement = Math.round((baseFinalDisplay * 0.04) / 12);
        const scenarioRetirement = Math.round((scenarioFinalDisplay * 0.04) / 12);

        return {
            baseTotal: baseFinalDisplay,
            scenarioTotal: scenarioFinalDisplay,
            difference,
            yearsDiff,
            baseRetirement,
            scenarioRetirement,
            retirementDiff: scenarioRetirement - baseRetirement,
        };
    }, [anyScenarioEnabled, effectiveParams, debouncedSettings, baseFinalDeterministic, currentWealth, expectedReturn]);

    // Total contributions vs growth
    const totalContributions = displayProjection[displayProjection.length - 1]?.contributions || 0;
    const growthAmount = finalAmount - totalContributions;

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto">
                <div className="card p-12 flex items-center justify-center">
                    <div className="animate-spin w-6 h-6 border-2 border-gray-200 border-t-accent-500 rounded-full" />
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto">
            {/* Warning banner if net worth data failed to load */}
            {wealthError && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                    <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-amber-800 text-sm">
                        Could not load your net worth data. Projections are using $0 as a starting amount. Check your linked accounts and try refreshing.
                    </p>
                </div>
            )}

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-10">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight">Your wealth projection</h1>
                    <p className="text-gray-600 mt-2 text-base sm:text-lg">
                        Based on {formatCurrencyCompact(settings.startingAmount ?? currentWealth)} invested today
                        {settings.startingAmount !== null ? (
                            <span className="ml-2 px-2 py-0.5 bg-indigo-50 text-indigo-600 text-sm font-medium rounded">
                                custom starting amount
                            </span>
                        ) : savingsData && savingsData.avgMonthlySavings > 0 ? (
                            <span className="ml-2 px-2 py-0.5 bg-accent-50 text-accent-600 text-sm font-medium rounded">
                                using your real savings data
                            </span>
                        ) : null}
                        {settings.adjustForInflation && (
                            <span className="ml-2 px-2 py-0.5 bg-accent-50 text-accent-600 text-sm font-medium rounded">
                                in today's dollars
                            </span>
                        )}
                    </p>
                </div>
                <button
                    onClick={() => setShowSettings(true)}
                    className="btn-secondary px-5 py-3 text-base w-full sm:w-auto"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Customize
                </button>
            </div>

            {/* Hero Insight */}
            <div className="card p-6 sm:p-10 mb-6 sm:mb-8 text-center">
                {goalReach ? (
                    <>
                        <p className="text-gray-500 mb-3 text-base sm:text-lg">At your current pace, you'll reach</p>
                        <p className="text-3xl sm:text-5xl font-semibold text-gray-900 mb-3 tracking-tight">{formatCurrencyCompact(settings.goal)}</p>
                        <p className="text-xl sm:text-2xl text-accent-600 font-semibold">
                            by {new Date().getFullYear() + goalReach.year}
                        </p>
                        <p className="text-gray-500 mt-4 text-sm sm:text-base">
                            That's {goalReach.year} years from now
                        </p>
                    </>
                ) : (
                    <>
                        <p className="text-gray-500 mb-3 text-base sm:text-lg">In {settings.years} years, you could have</p>
                        <p className="text-3xl sm:text-5xl font-semibold text-gray-900 mb-3 tracking-tight">{formatCurrencyCompact(finalAmount)}</p>
                        <p className="text-sm sm:text-base text-gray-500 mt-4">
                            {probability.toFixed(0)}% chance of reaching {formatCurrencyCompact(settings.goal)}
                        </p>
                    </>
                )}
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="card p-6">
                    <InsightCard
                        label="Probability"
                        value={`${probability.toFixed(0)}%`}
                        subtext="of reaching goal"
                        color={probability >= 70 ? "text-accent-600" : probability >= 50 ? "text-amber-600" : "text-danger-500"}
                        tooltip={`Based on 1,000 Monte Carlo simulations using your ${settings.allocation.stocks}% stocks / ${settings.allocation.bonds}% bonds allocation, ${settings.monthlyContribution.toLocaleString()}/month contribution, and ${settings.annualIncrease}% annual increase.`}
                    />
                </div>
                <div className="card p-6">
                    <InsightCard
                        label="Projected"
                        value={formatCurrencyShort(finalAmount)}
                        subtext={`in ${settings.years} years`}
                        color="text-accent-600"
                        tooltip={`The median (P50) outcome from simulations. This means half of scenarios end higher and half end lower. Your ${formatCurrencyShort(totalContributions)} in contributions + ${formatCurrencyShort(growthAmount)} in expected investment growth.`}
                    />
                </div>
                <div className="card p-6">
                    <InsightCard
                        label="Retirement Income"
                        value={formatCurrencyCompact(monthlyRetirementIncome)}
                        subtext="per month (4% rule)"
                        color="text-gray-900"
                        tooltip={`Based on the 4% safe withdrawal rate. This means withdrawing 4% of your portfolio annually (${formatCurrencyCompact(finalAmount * 0.04)}/year) which historically has sustained retirees for 30+ years.`}
                    />
                </div>
            </div>

            {/* Progress Bar */}
            <div className="card p-4 sm:p-6 mb-6 sm:mb-8">
                <ProgressBar current={currentWealth} goal={settings.goal} />
            </div>

            {/* Chart */}
            <div className="card p-4 sm:p-8 mb-6 sm:mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Growth trajectory</h3>
                    <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-600">
                        <span className="flex items-center gap-2">
                            <span className="w-4 h-0.5 bg-accent-500 rounded"></span> Projected
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="w-4 h-0.5 bg-amber-500 rounded" style={{borderStyle: 'dashed'}}></span> Goal
                        </span>
                    </div>
                </div>
                <ProjectionChart
                    data={displayProjection}
                    goal={settings.goal}
                    milestones={milestones}
                    showRange={settings.showRange}
                    selectedMilestone={selectedMilestone}
                />
                {/* Milestone labels - only show reachable milestones */}
                {(() => {
                    const reachableMilestones = milestones.filter(m => findYearToReachGoal(displayProjection, m));
                    if (reachableMilestones.length === 0) return null;
                    return (
                        <div className="flex flex-wrap items-center justify-center gap-3 mt-6 pt-6 border-t border-gray-200">
                            <span className="text-sm text-gray-500">Milestones:</span>
                            {reachableMilestones.map((m) => {
                                const yearToReach = findYearToReachGoal(displayProjection, m);
                                const isSelected = selectedMilestone === m;
                                return (
                                    <button
                                        key={m}
                                        onClick={() => setSelectedMilestone(isSelected ? null : m)}
                                        className={`text-sm px-3 py-1.5 rounded-lg transition-colors font-medium ${
                                            isSelected
                                                ? 'bg-amber-500 text-white'
                                                : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        {formatCurrencyShort(m)} ({yearToReach.year}y)
                                    </button>
                                );
                            })}
                        </div>
                    );
                })()}
            </div>

            {/* Scenario Builder */}
            <div className="card p-4 sm:p-8 mb-6 sm:mb-8">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 text-center">Scenario builder</h3>
                <p className="text-sm text-gray-500 text-center mb-6">Toggle scenarios to see how they change your projection</p>

                {/* Cumulative Impact Summary */}
                {scenarioImpact && (
                    <div className="mb-6 p-5 rounded-xl bg-accent-50 border border-accent-200">
                        <p className="text-sm text-gray-600 mb-2 text-center">With these changes</p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mb-3">
                            <span className="text-lg text-gray-500">{formatCurrencyShort(scenarioImpact.baseTotal)}</span>
                            <svg className="w-5 h-5 text-accent-600 rotate-90 sm:rotate-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                            <span className="text-2xl font-semibold text-gray-900 tracking-tight">{formatCurrencyShort(scenarioImpact.scenarioTotal)}</span>
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
                            <span className={`font-medium ${scenarioImpact.difference >= 0 ? 'text-accent-600' : 'text-danger-500'}`}>
                                {scenarioImpact.difference >= 0 ? '+' : ''}{formatCurrencyShort(scenarioImpact.difference)}
                            </span>
                            {scenarioImpact.yearsDiff > 0 && (
                                <span className="text-accent-600 font-medium">
                                    {scenarioImpact.yearsDiff} {scenarioImpact.yearsDiff === 1 ? 'year' : 'years'} sooner
                                </span>
                            )}
                            {scenarioImpact.yearsDiff < 0 && (
                                <span className="text-danger-500 font-medium">
                                    {Math.abs(scenarioImpact.yearsDiff)} {Math.abs(scenarioImpact.yearsDiff) === 1 ? 'year' : 'years'} later
                                </span>
                            )}
                            <span className={`font-medium ${scenarioImpact.retirementDiff >= 0 ? 'text-accent-600' : 'text-danger-500'}`}>
                                {scenarioImpact.retirementDiff >= 0 ? '+' : ''}{formatCurrencyCompact(scenarioImpact.retirementDiff)}/mo retirement income
                            </span>
                        </div>
                    </div>
                )}

                {/* Scenario Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <ScenarioCard
                        id="increaseSavings"
                        label="Increase monthly savings"
                        description={`Default: 25% of your ${formatCurrencyCompact(settings.monthlyContribution)}/mo contribution`}
                        enabled={scenarios.increaseSavings.enabled}
                        amount={scenarios.increaseSavings.amount}
                        onToggle={handleScenarioToggle}
                        onAmountChange={handleScenarioAmountChange}
                        suffix="/month"
                    />

                    {savingsData && savingsData.avgMonthlyExpenses > 0 && (
                        <ScenarioCard
                            id="cutExpenses"
                            label="Cut expenses"
                            description={`Default: 20% of your ${formatCurrencyCompact(savingsData.avgMonthlyExpenses)}/mo spending`}
                            enabled={scenarios.cutExpenses.enabled}
                            amount={scenarios.cutExpenses.amount}
                            onToggle={handleScenarioToggle}
                            onAmountChange={handleScenarioAmountChange}
                            suffix="/month"
                        />
                    )}

                    <ScenarioCard
                        id="max401k"
                        label="Max out 401(k)"
                        description="$23,500/year ($1,958/month) added to contributions"
                        enabled={scenarios.max401k.enabled}
                        amount={scenarios.max401k.amount}
                        onToggle={handleScenarioToggle}
                        onAmountChange={handleScenarioAmountChange}
                        suffix="/month"
                    />

                    <ScenarioCard
                        id="lumpSum"
                        label="Invest a lump sum today"
                        description="One-time amount added to your starting balance"
                        enabled={scenarios.lumpSum.enabled}
                        amount={scenarios.lumpSum.amount}
                        onToggle={handleScenarioToggle}
                        onAmountChange={handleScenarioAmountChange}
                    />

                    <ScenarioCard
                        id="rothIRA"
                        label="Start Roth IRA"
                        description="$7,000/year ($583/month) added to contributions"
                        enabled={scenarios.rothIRA.enabled}
                        amount={scenarios.rothIRA.amount}
                        onToggle={handleScenarioToggle}
                        onAmountChange={handleScenarioAmountChange}
                        suffix="/month"
                    />

                    {debouncedSettings.allocation.stocks < 85 && (
                        <ScenarioCard
                            id="aggressiveAllocation"
                            label="Switch to aggressive allocation"
                            description="90% stocks / 5% bonds / 5% cash"
                            enabled={scenarios.aggressiveAllocation.enabled}
                            onToggle={handleScenarioToggle}
                        />
                    )}
                </div>
            </div>

            {/* Disclaimer */}
            <p className="text-sm text-gray-400 text-center pb-8">
                Projections are based on historical averages and Monte Carlo simulation.
                Actual results will vary. Past performance doesn't guarantee future results.
            </p>

            {/* Settings Panel */}
            <SettingsPanel
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                settings={settings}
                setSettings={setSettings}
            />
        </div>
    );
}
